import React, {Fragment, useCallback, useState} from "react";
import {RowContext, TableContext, TableOperation, TableV2} from "@q4us-sw/q4us-ui/lib/TableV2";
import {Trans} from "react-i18next";
import {dashboard_attach_group, dashboard_detach_group, dashboard_users, emAssignment, getFeConfig} from "../api/api";
import {Button, Col, Input, Modal, notification, PageHeader, Row, Tooltip} from "antd";
import {ApiOutlined, DisconnectOutlined, EyeOutlined} from '@ant-design/icons'
import "../styles/UserManagement.css"
import {DefaultProps, RefTable} from "./RefTable"
import {unstable_batchedUpdates} from "react-dom";

export const HeaderContext = React.createContext()

export const HeaderContextProvider = (props)=>{
    const [clear, setClear] = React.useState()
    return <HeaderContext.Provider value={{clear, setClear}}>
        {props.children}
    </HeaderContext.Provider>
}

const Header = ({isAssigned, headerTitle, user, value}) => {
    const [data, setData] = useState()

    const {Search} = Input
    return <Fragment>
        <PageHeader title={headerTitle}/>
        <TableContext.Consumer>
            {table => <HeaderContext.Consumer>
                {header => <Search
                    className={'table-search'}
                    allowClear
                    placeholder={'Search by Name'}
                    value={value!==undefined?value:data}
                    onChange={e => {
                        if(header?.clear!==undefined) header?.setClear(undefined)
                        setData(e.target.value)
                    }}
                    onSearch={async value => {
                        const payload = {
                            is_assigned: isAssigned,
                            user_id: user.id,
                            filter: [{name: 'group_name', value, comparator: 'contains'}],
                            page: {size: table.page.size || 100, from: ((table.page.current || 1) - 1) * (table.page.size || 100)}
                        }
                        const {data: {rows =[], page ={}} ={}} = await emAssignment(payload)
                        table.setData(rows)
                        table.setPage(page);
                    }}
                />}
            </HeaderContext.Consumer>}
        </TableContext.Consumer>
    </Fragment>
}

class LinkContextHandler{
    refs = {}
    get(key) {
        return this.refs[key];
    }
    set(key, ctx) {
        this.refs[key] = ctx
    }
}

export const LinkContext = React.createContext(new LinkContextHandler())

export const LinkContextProvider = (props)=>{
    const [ctx] = React.useState(new LinkContextHandler())
    return <LinkContext.Provider value={ctx}>
        {props.children}
    </LinkContext.Provider>
}

const EMAssignmentItem = ({isAssigned, headerTitle, tooltip, btnIcon, user, fetchData, handleClick, children}) => {
    return <HeaderContextProvider>
        <HeaderContext.Consumer>
            {header => <div className={'margin-five'}>
                <TableV2
                    schema={'em_auth_groups'}
                    header={<Header isAssigned={isAssigned} headerTitle={headerTitle} user={user} value={header.clear}/> }
                    fetchConfig={async (schema) => {
                        return await getFeConfig(schema)
                    }}
                    fetchData={fetchData}
                    action={{
                        name: 'id',
                        title: '',
                        options: TableOperation.Custom
                    }}
                    getCustomAction={opt => <RowContext.Consumer>
                        {row => <Tooltip
                            title={tooltip.title}
                            color={tooltip.color}
                            mouseEnterDelay={0.5}
                        >
                            <Button
                                type={"primary"}
                                danger={isAssigned}
                                icon={btnIcon}
                                onClick={() => {
                                    header?.setClear(null)
                                    handleClick(row?.row.group_id, row?.row.group_name)
                                }}
                            />
                        </Tooltip>}
                    </RowContext.Consumer>}
                    scroll={{y: '50vh', x: 'max-content'}}
                >{children}</TableV2>
            </div>}
        </HeaderContext.Consumer>
    </HeaderContextProvider>
}

const EMAssignment = ({user}) => {
    const refreshTableData = useCallback(async ctx => {
        unstable_batchedUpdates(() => {
            ctx.get('assign').setLoading(true)
            ctx.get('unAssign').setLoading(true)
        })
        const assignPayload = {
            is_assigned: true,
            user_id: user.id,
            page: {size: ctx.get('assign')?.page?.size || 100, from: ((ctx.get('assign')?.page?.current || 1) - 1) * (ctx.get('assign')?.page?.size || 100)}
        }
        const unAssignPayload = {
            is_assigned: false,
            user_id: user.id,
            page: {size: ctx.get('unAssign')?.page?.size || 100, from: ((ctx.get('unAssign')?.page?.current || 1) - 1) * (ctx.get('unAssign')?.page?.size || 100)}
        }
        const {data: {rows: assignRows =[], page: assignPage ={}} ={}} = await emAssignment(assignPayload)
        const {data: {rows: unAssignRows =[], page: unAssignPage ={}} ={}} = await emAssignment(unAssignPayload)
        unstable_batchedUpdates(() => {
            ctx.get('assign').setData(assignRows)
            ctx.get('assign').setPage(assignPage)
            ctx.get('unAssign').setData(unAssignRows)
            ctx.get('unAssign').setPage(unAssignPage)
            ctx.get('assign').setLoading(false)
            ctx.get('unAssign').setLoading(false)
        })
    }, [user])

    return <LinkContextProvider>
        <LinkContext.Consumer>
            {ctx => <Row>
                <Col span={12}>
                    <EMAssignmentItem
                        isAssigned
                        headerTitle={'Assigned E-Merchants'}
                        tooltip={{title: 'Remove', color: 'orange'}}
                        btnIcon={<DisconnectOutlined/>}
                        user={user}
                        fetchData={async (request) => {
                            const payload = {
                                is_assigned: true,
                                user_id: user.id,
                                page: {size: request.page?.size || 100, from: ((request.page?.current || 1) - 1) * (request.page?.size || 100)}
                            }
                            return await emAssignment(payload)
                        }}
                        handleClick={async (group_id, group_name) => {
                            const {status} = await dashboard_detach_group(user.id, group_id)
                            if(status===200) {
                                const message = group_name + ' unassigned successfully'
                                notification.success({message: <Trans i18nKey={message} defaults={message}/>})
                                await refreshTableData(ctx)
                            }
                        }}
                    ><TableContext.Consumer>{table => ctx.set('assign', table)}</TableContext.Consumer></EMAssignmentItem>
                </Col>
                <Col span={12}>
                    <EMAssignmentItem
                        isAssigned={false}
                        headerTitle={'Available E-Merchants'}
                        tooltip={{title: 'Assign', color: '#2db7f5'}}
                        btnIcon={<ApiOutlined/>}
                        user={user}
                        fetchData={async (request) => {
                            const payload = {
                                is_assigned: false,
                                user_id: user.id,
                                page: {size: request.page?.size || 100, from: ((request.page?.current || 1) - 1) * (request.page?.size || 100)}
                            }
                            return await emAssignment(payload)
                        }}
                        handleClick={async (group_id, group_name) => {
                            const {status} = await dashboard_attach_group(user.id, group_id)
                            if(status===200) {
                                const message = group_name + ' assigned successfully'
                                notification.success({message: <Trans i18nKey={message} defaults={message}/>})
                                await refreshTableData(ctx)
                            }
                        }}
                    ><TableContext.Consumer>{table => ctx.set('unAssign', table)}</TableContext.Consumer></EMAssignmentItem>
                </Col>
            </Row>}
        </LinkContext.Consumer>
    </LinkContextProvider>
}

export const Assign  = ({row})=>{
    const [open, setOpen] = React.useState(false)

    return <React.Fragment>
        <Button icon={<EyeOutlined/>} onClick={() => {
            setOpen(true)
        }}/>
        {open && <Modal
            centered={true}
            closable={true}
            key={row.id}
            width={'60vw'}
            onCancel={() => {
                setOpen(false)
            }}
            visible={open}
            title={`EM Role Attachment for user [${row.email || row.username}]`}
            footer={null}
        >
            <EMAssignment user={row}/>
        </Modal>}
    </React.Fragment>
}


export const UserManagement = (props)=><RefTable {...props}/>;

UserManagement.defaultProps = {
    ...DefaultProps,
    title: "Dashboard Users",
    tableSchema: "user_management",
    add: false,
    fetch: async (tableName, filters = {}, page = {})=> dashboard_users("all", {page,query: filters}),
    tableName: "dashboard_users",
    edit: false,
    tfootHeight: 50,
    filterSchema: "user_filter",
    enrichColumns: async (columns) => {
        return [
            {
                name: "id",
                fixed: true,
                title: "",
                width: 120,
                formatter: (row) => <Assign row={row}/>
            }, ...columns]
    },
}
